/**
 * RequirementActionsContext.jsx — Phase 9 (2026-02).
 *
 * Parallel to AssetActionsContext. A React context that lets any Art-Dept
 * surface open the shared RequirementModal and register a one-shot
 * post-save hook.
 *
 * Usage:
 *   const { openRequirementModal } = useRequirementActions();
 *   openRequirementModal({
 *     prefill: { linked_scene_ids: [sid] },
 *     onCreated: async (saved) => { setRequirements(prev => [...prev, saved]); },
 *   });
 *
 * The provider must wrap the ArtDepartmentPage tree; the modal itself is
 * rendered inside the provider so a single instance is shared.
 *
 * Fetch policy: fetches scenes + locations INDEPENDENTLY of
 * AssetActionsContext. Rationale — (a) both providers live at the same
 * level in ArtDepartmentPage's per-tab tree, so sharing would require
 * hoisting a common ancestor; the existing tree pattern from Phase 8
 * keeps AssetActionsProvider as the outer wrapper per tab, and adding a
 * common data provider would touch every tab branch. (b) Both fetches
 * are already cached client-side by React state and the network cost
 * is one small aggregate call per surface. Duplicate fetch is
 * acceptable and matches how AssetActionsContext handles it too.
 * Documented per exit-gate §3 in the Phase 9 brief.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import RequirementModal from './RequirementModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

export function useRequirementActions() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRequirementActions must be used inside <RequirementActionsProvider>');
  return v;
}

export function RequirementActionsProvider({ projectId, token, children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [req, setReq] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [locations, setLocations] = useState([]);
  const onCreatedRef = useRef(null);

  useEffect(() => {
    if (!projectId || !token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    (async () => {
      try {
        const [br, locs] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/breakdown`, h),
          axios.get(`${API}/projects/${projectId}/art/locations`, h).catch(() => ({ data: [] })),
        ]);
        const list = Array.isArray(br.data) ? br.data : (br.data?.scenes || []);
        setScenes(list.map(s => ({ id: s.id, scene_number: s.scene_number })));
        setLocations(locs.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [projectId, token]);

  const openRequirementModal = useCallback(({ mode: m = 'create', prefill: pf = null, req: r = null, onCreated = null } = {}) => {
    setMode(m);
    setReq(r);
    setPrefill(pf);
    onCreatedRef.current = onCreated;
    setOpen(true);
  }, []);

  const editRequirement = useCallback((r) => openRequirementModal({ mode: 'edit', req: r }), [openRequirementModal]);

  const onSaved = useCallback(async (saved) => {
    try { if (onCreatedRef.current) await onCreatedRef.current(saved); }
    catch (e) { console.error('onCreated hook failed', e); }
    finally { onCreatedRef.current = null; }
  }, []);

  const onDeleted = useCallback(() => {}, []);
  const onClose = useCallback(() => { setOpen(false); onCreatedRef.current = null; }, []);

  const value = useMemo(
    () => ({ openRequirementModal, editRequirement, scenes, locations }),
    [openRequirementModal, editRequirement, scenes, locations],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <RequirementModal
        open={open}
        mode={mode}
        req={req}
        prefill={prefill}
        projectId={projectId}
        token={token}
        scenes={scenes}
        locations={locations}
        onClose={onClose}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </Ctx.Provider>
  );
}
